import { DeleteRounded as DeleteRoundedIcon } from '@mui/icons-material'
import { Button, Chip, Container, Grid, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import Can from '../../Layout/Can/Can.jsx'
import ZList from '../../Shared/Components/ZList.jsx'
import { useGlobalContext } from '../../Shared/Contexts/GlobalContext.jsx'
import ConfirmWithCheckDialog from '../../Shared/Dialogs/ConfirmWithCheckDialog.jsx'
import OrganizationProfileCard from '../Card/OrganizationProfileCard.jsx'
import { useOrganizationProfiles } from '../Contexts/OrganizationProfilesContext.jsx'
import AddOrganizationProfile from '../Dialogs/AddOrganizationProfile.jsx'
import EditOrganizationProfile from '../Dialogs/EditOrganizationProfile.jsx'

const DEFAULT_PAGE_SIZE = 25

const OrganizationProfiles = () => {
  const [searchParams] = useSearchParams()
  const fullText = searchParams.get('search')

  const { useFetchOrganizationProfiles, useDeleteOrganizationProfile } = useOrganizationProfiles()
  const { openErrorAlert, openSuccessAlert } = useGlobalContext()

  const [page, setPage] = useState(1)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [profiles, setProfiles] = useState({ data: [], count: 0 })
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState(null)
  const [isConfirmWithCheckOpen, setIsConfirmWithCheckOpen] = useState(false)

  const fetchProfiles = ({ page }) => {
    setIsLoading(true)
    useFetchOrganizationProfiles(fullText ? {
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      fullText
    } : {
      page,
      pageSize: DEFAULT_PAGE_SIZE
    }).then((res) => setProfiles(res))
      .catch(() => openErrorAlert({ keys: ['organizationProfiles.fetchError'] }))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    fetchProfiles({ page })
  }, [page])

  useEffect(() => {
    fetchProfiles({ page: 1 })
    setPage(1)
  }, [fullText])

  const handleCreateProfile = () => {
    setSelectedProfile(null)
    setIsAddDialogOpen(true)
  }

  const handleChangePage = (_, value) => setPage(value)

  const handleProfileEdit = (profile) => {
    setSelectedProfile(profile)
    setIsEditDialogOpen(true)
  }

  const handleAddDialogClose = () => {
    fetchProfiles({ page: 1 })
    setPage(1)
    setIsAddDialogOpen(false)
  }

  const handleEditDialogClose = () => {
    fetchProfiles({ page: 1 })
    setPage(1)
    setIsEditDialogOpen(false)
    setSelectedProfile(null)
  }

  const openDeleteDialog = (profile) => {
    setConfirmDialogData({
      icon: (
        <DeleteRoundedIcon
          sx={{ fontSize: 60, color: '#ef5350' }}
        />
      ),
      questionText: t('organizationProfiles.deleteQuestion'),
      subQuestionText: profile.name,
      copyText: profile.id.toString(),
      bodyText: t('organizationProfiles.confirmIdToProceed'),
      inputSuggestionText: t('organizationProfiles.pasteId')
    })
    setSelectedProfile(profile)
    setIsConfirmWithCheckOpen(true)
  }

  const handleConfirmDelete = () => {
    useDeleteOrganizationProfile(selectedProfile.id)
      .then(() => {
        setSelectedProfile(null)
        openSuccessAlert()
        setIsConfirmWithCheckOpen(false)
        fetchProfiles({ page: 1 })
        setPage(1)
      })
      .catch((err) => {
        openErrorAlert({ keys: err.keys || ['organizationProfiles.deleteError'] })
      })
  }

  const handleCloseConfirmWithCheck = () => {
    setIsConfirmWithCheckOpen(false)
    setSelectedProfile(null)
  }

  const profilesList = profiles?.data?.map((profile, i) => (
    <OrganizationProfileCard
      index={i}
      key={`profile-${profile.id}`}
      profile={profile}
      onDelete={openDeleteDialog}
      onEdit={handleProfileEdit}
    />
  ))

  return (
    <Can action={'organizations_profiles_r'}>
      <Container
        maxWidth="lg"
        sx={{ py: 3 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="flex-start">
          <Grid>
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={2}>
              <Typography
                id="organization-profiles-header"
                variant="h2">
                {t('organizationProfiles.title')}
              </Typography>
              <Chip
                label={profiles?.data?.length || 0}
                color="primary"
              />
            </Stack>
          </Grid>

          <Grid>
            <Can action={'organizations_profiles_c'}>
              <Button
                id="create-organization-profile-button"
                variant="contained"
                color="primary"
                onClick={handleCreateProfile}>
                {t('organizationProfiles.create')}
              </Button>
            </Can>
          </Grid>
        </Grid>

        <ZList
          count={profiles?.data?.length}
          entities={profilesList}
          page={page}
          onPageChange={handleChangePage}
          isLoading={isLoading}
        />

        <AddOrganizationProfile
          isOpen={isAddDialogOpen}
          onClose={handleAddDialogClose}
        />

        <EditOrganizationProfile
          isOpen={isEditDialogOpen}
          profile={selectedProfile}
          onClose={handleEditDialogClose}
        />

        {confirmDialogData && (
          <ConfirmWithCheckDialog
            isOpen={isConfirmWithCheckOpen}
            onClose={handleCloseConfirmWithCheck}
            onConfirmAction={handleConfirmDelete}
            icon={confirmDialogData.icon}
            questionText={confirmDialogData.questionText}
            subQuestionText={confirmDialogData.subQuestionText}
            copyText={confirmDialogData.copyText}
            bodyText={confirmDialogData.bodyText}
            inputSuggestionText={confirmDialogData.inputSuggestionText}
          />
        )}
      </Container>
    </Can>
  )
}

export default OrganizationProfiles
